import { medicationEntryMatchesTask } from './medicationTaskDose';
import { getShiftBlockIdForTask } from './shiftTimeBlocks';

const TYPE_LEVEL_NOTE_TYPES = new Set([
  'progress_note',
  'handover',
  'admission',
  'discharge',
  'care_plan',
]);

function hasVitalValue(vitals, key) {
  if (!vitals) return false;
  if (key === 'bp') {
    return (
      (vitals.bp_systolic != null && vitals.bp_systolic !== '')
      || (vitals.bp_diastolic != null && vitals.bp_diastolic !== '')
    );
  }
  const value = vitals[key];
  return value != null && value !== '';
}

function vitalKeysForTaskLabel(itemLabel) {
  const label = String(itemLabel || '').toLowerCase();
  if (label.includes('blood pressure') || label === 'bp') return ['bp'];
  if (label.includes('pulse') || label.includes('heart rate')) return ['pulse'];
  if (label.includes('temp')) return ['temp_celsius'];
  if (label.includes('o2') || label.includes('oxygen') || label.includes('spo2')) return ['o2_sat'];
  if (label.includes('respiratory') || label === 'rr') return ['rr'];
  if (label.includes('pain')) return ['pain'];
  if (label.includes('weight')) return ['weight_kg'];
  if (label.includes('blood sugar') || label.includes('glucose')) return ['blood_sugar'];
  return [];
}

function taskDocumentsVitals(task, vitals) {
  const keys = vitalKeysForTaskLabel(task.itemLabel || task.title);
  if (!keys.length) return false;
  return keys.some((key) => hasVitalValue(vitals, key));
}

function manualTaskDocumented(task, noteType, content) {
  const title = String(task.title || '').toLowerCase();
  if (noteType !== 'vitals' || !content?.vitals) return false;

  if (title.includes('blood sugar') || title.includes('glucose')) {
    return hasVitalValue(content.vitals, 'blood_sugar');
  }
  if (title.includes('pain assessment')) {
    return hasVitalValue(content.vitals, 'pain');
  }
  return false;
}

function isInTaskWindow(task, noteTime) {
  if (!task.windowStart || !task.windowEnd) return true;
  const start = new Date(task.windowStart);
  const end = new Date(task.windowEnd);
  if (task.status === 'overdue') {
    return noteTime >= start;
  }
  return noteTime >= start && noteTime <= end;
}

function closestTask(tasks, noteTime) {
  return [...tasks].sort((a, b) => {
    const aDue = a.dueTime ? new Date(a.dueTime).getTime() : noteTime.getTime();
    const bDue = b.dueTime ? new Date(b.dueTime).getTime() : noteTime.getTime();
    return Math.abs(aDue - noteTime.getTime()) - Math.abs(bDue - noteTime.getTime());
  })[0];
}

/**
 * Progress note / handover / etc.: complete the closest pending task of that
 * note type in the same shift block — even if the narrow due-window has not
 * opened yet (e.g. submit at 16:37 for a 17:22 progress note).
 */
export function getTypeLevelTaskIdsCompletedByNote(tasks, noteType, noteTime) {
  if (!TYPE_LEVEL_NOTE_TYPES.has(noteType) || !tasks?.length || !noteTime) return [];

  const pending = tasks.filter(
    (t) =>
      (t.status === 'pending' || t.status === 'overdue')
      && t.sourceNoteType === noteType
  );
  if (!pending.length) return [];

  const noteBlock = getShiftBlockIdForTask(
    { dueTime: noteTime instanceof Date ? noteTime : new Date(noteTime) },
    noteTime instanceof Date ? noteTime : new Date(noteTime)
  );
  const inBlock = pending.filter(
    (task) => getShiftBlockIdForTask(task, noteTime) === noteBlock
  );
  // Prefer in-window tasks inside this shift block; never complete a different
  // block's overdue task just because noteTime is after its windowStart.
  const inWindowAndBlock = inBlock.filter((task) => isInTaskWindow(task, noteTime));
  const pool = inWindowAndBlock.length ? inWindowAndBlock : inBlock;
  if (!pool.length) return [];

  const closest = closestTask(pool, noteTime);
  return closest ? [closest.id] : [];
}

/**
 * Complete pending tasks that are documented in a saved note (vitals values, etc.).
 * Returns task ids that should be completed.
 */
export function getTaskIdsCompletedByNote(tasks, noteType, noteContent, noteTime) {
  if (!tasks?.length || !noteContent) return [];
  console.log('[TaskDebug] contentMatcher:start', {
    noteType,
    noteTime: noteTime?.toISOString?.() || String(noteTime),
    pendingCount: tasks.filter((t) => ['pending', 'overdue'].includes(t.status)).length,
    vitals: noteContent?.vitals || null,
  });

  const pending = tasks.filter((t) => ['pending', 'overdue'].includes(t.status));
  const toComplete = new Set(getTypeLevelTaskIdsCompletedByNote(tasks, noteType, noteTime));

  if (noteType === 'vitals' && noteContent.vitals) {
    const vitals = noteContent.vitals;
    const noteBlock = getShiftBlockIdForTask(
      { dueTime: noteTime instanceof Date ? noteTime : new Date(noteTime) },
      noteTime instanceof Date ? noteTime : new Date(noteTime)
    );
    const recurringGroups = new Map();

    pending.forEach((task) => {
      if (task.sourceNoteType !== 'vitals') return;
      if (!taskDocumentsVitals(task, vitals)) return;
      // Same shift block only — avoids morning overdue vitals completing from afternoon docs.
      if (getShiftBlockIdForTask(task, noteTime) !== noteBlock) return;

      const groupKey = task.itemLabel || task.title || 'vitals';
      if (!recurringGroups.has(groupKey)) recurringGroups.set(groupKey, []);
      recurringGroups.get(groupKey).push(task);
    });
    console.log('[TaskDebug] contentMatcher:recurringGroups', Array.from(recurringGroups.entries()).map(([key, arr]) => ({
      key,
      taskIds: arr.map((t) => t.id),
    })));

    recurringGroups.forEach((groupTasks) => {
      const closest = closestTask(groupTasks, noteTime);
      if (closest) toComplete.add(closest.id);
    });

    const manualGroups = new Map();
    pending.forEach((task) => {
      if (task.type !== 'manual') return;
      if (!manualTaskDocumented(task, noteType, noteContent)) return;
      if (getShiftBlockIdForTask(task, noteTime) !== noteBlock) return;

      const groupKey = task.title || 'manual';
      if (!manualGroups.has(groupKey)) manualGroups.set(groupKey, []);
      manualGroups.get(groupKey).push(task);
    });
    console.log('[TaskDebug] contentMatcher:manualGroups', Array.from(manualGroups.entries()).map(([key, arr]) => ({
      key,
      taskIds: arr.map((t) => t.id),
    })));

    manualGroups.forEach((groupTasks) => {
      const closest = closestTask(groupTasks, noteTime);
      if (closest) toComplete.add(closest.id);
    });
  }

  if (noteType === 'medication_admin') {
    const meds = noteContent.medications_administered || noteContent.medications || [];
    if (meds.length) {
      const usedTaskIds = new Set();
      // Meds still use due-windows so overdue morning doses can be caught up later.
      const eligibleTasks = pending
        .filter((task) => task.sourceNoteType === 'medication_admin')
        .filter((task) => isInTaskWindow(task, noteTime))
        .sort((a, b) => {
          const aDue = a.dueTime ? new Date(a.dueTime).getTime() : 0;
          const bDue = b.dueTime ? new Date(b.dueTime).getTime() : 0;
          return aDue - bDue;
        });

      meds.forEach((med) => {
        const match = eligibleTasks.find(
          (task) => !usedTaskIds.has(task.id) && medicationEntryMatchesTask(task, med)
        );
        if (match) {
          toComplete.add(match.id);
          usedTaskIds.add(match.id);
        }
      });
    }
  }

  console.log('[TaskDebug] contentMatcher:result', Array.from(toComplete));
  return Array.from(toComplete);
}

export function getVitalKeysWithValues(vitals = {}) {
  const keys = [];
  if (hasVitalValue(vitals, 'bp')) keys.push('bp');
  if (hasVitalValue(vitals, 'pulse')) keys.push('pulse');
  if (hasVitalValue(vitals, 'temp_celsius')) keys.push('temp_celsius');
  if (hasVitalValue(vitals, 'o2_sat')) keys.push('o2_sat');
  if (hasVitalValue(vitals, 'rr')) keys.push('rr');
  if (hasVitalValue(vitals, 'pain')) keys.push('pain');
  if (hasVitalValue(vitals, 'weight_kg')) keys.push('weight_kg');
  if (hasVitalValue(vitals, 'blood_sugar')) keys.push('blood_sugar');
  return keys;
}

export { TYPE_LEVEL_NOTE_TYPES };
